import { useRef, useEffect, useState } from "react";

import { AnimatePresence } from "framer-motion";
import useMeasure from "react-use-measure";
import { _project } from "state/store.projects";
import { _isometry } from "state/store.isometry";
import { active_project } from "state/store.global";
import { iso_free } from "state/store.global";
import { iso_isrotating } from "state/store.global";
import { getFocus, setFocus, useGet } from "state/jotai";
import { getIsoData, setIsoData } from "state/jotai.isometryDataHooks";
import { getProjectData } from "state/jotai.projectDataHooks";

import { useBreak } from "hooks/useBreak";

import { Space } from "./Isometry.style";
import { Cursor } from "./Isometry.Cursor";
import { createZppSetup } from "./Isometry.ZoomPanPinch";
import { Canvas } from "./Isometry.Canvas";
import { Center } from "./Isometry.Center";
import { Tiny } from "./Isometry.Tiny";
import { Svg } from "./Isometry.Svg";
import { Controls } from "./Isometry.Controls";
import { Measure } from "./Isometry.Measure";
import { Recenter } from "./isometry.Recenter";
import { Loading2 } from "components/Loading2/Loading2";
import { ZoomPanPinch } from "components/ZoomPanPinch/ZoomPanPinch";
import { Compas } from "components/Compas/Compas";
import { ColorLegend } from "components/ColorLegend/ColorLegend";
import { NoSnapSlider } from "components/IsometryControls/NoSnapSlider";
import { Play } from "components/IsometryRotation/Play";
import { SnapOnRotationEnd } from "components/IsometrySnapOnRotationEnd/SnapOnRotationEnd";
import { Status } from "components/StatusPopup/StatusPopup";
import { Mode360 } from "components/IsometryRotation/360";
import { drawer_animation_done } from "state/store.global";

import { Overview } from "components/OverviewLabel/Overview";
import { Labels } from "components/OverviewLabel2/Overview.Labels";
import { _bootstrap } from "state/store.bootstrap";
import { ViewMap } from "pages/View.Map";
import { Box } from "@mui/material";
import { OverviewBack } from "./OverviewBack";
import Lottie from "lottie-react";
import mediaLoader from "/src/assets/animationjson/tip.json";

export const Isometry = () => {
  const [measure, bounds] = useMeasure();
  const [isLottieVisible, setLottieVisible] = useState(true); // Controls visibility of the Lottie animation
  const inactivityTimer = useRef(null); // Ref to track inactivity timer
  const lottieTimer = useRef(null); // Ref to control Lottie visibility duration


  const { pin, pid } = useGet(active_project);
  const projectType = getFocus(_bootstrap, "type");

  const showSVG = useGet(drawer_animation_done);

  const mode = true;
  const tiny = getIsoData("tiny", "src");
  const frames = getIsoData("frames");
  const percent = getIsoData("percent");
  const status = getIsoData("status");
  const config = getIsoData("config");
  const frame = getIsoData("activeframe");
  const svg = getIsoData("overlays", frame);
  const index = getIsoData("snapindex");
  const units = getProjectData("units", "Units");

  const setIndex = setIsoData("snapindex");
  const setFrame = setIsoData("activeframe");
  const setScale = setFocus(_isometry, "activescale");
  const scale = getFocus(_isometry, "activescale");

  const zppRef = useRef();
  const canRef = useRef();
  const dim = { W: config?.IMAGE_W, H: config?.IMAGE_H };
  const point = config?.CENTER;
  const zppsetup = createZppSetup(bounds, zppRef, dim, setScale, mode);
  const rmode = useGet(iso_free);
  const isrotate = useGet(iso_isrotating);
  const buttonsLabel = getFocus(_project, "buttonsLabel")?.[pin]?.[pid] ?? [];
  const isMobile = useBreak("md_dn");

  const handleLottieClick = () => {
    setLottieVisible(false); // Hide Lottie animation
    clearTimeout(lottieTimer.current); // Clear any active Lottie visibility timer
  };

  // Reset inactivity timer on user activity
  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer.current); // Clear previous inactivity timer

    // Set a new inactivity timer
    inactivityTimer.current = setTimeout(() => {
      // Show Lottie after 30 seconds of inactivity
      setLottieVisible(true);

      // Hide Lottie automatically after 10 seconds
      lottieTimer.current = setTimeout(() => setLottieVisible(false), 10000);
    }, 30000); // 30 seconds
  };
  //  useEffect(() => {
  //   // Set a timer to remove Lottie after 10 seconds
  //   const timer = setTimeout(() => {
  //     setLottieVisible(false);
  //   }, 10000); // 10 seconds

  //   // Clear the timer if the component unmounts or on cleanup
  //   return () => clearTimeout(timer);
  // }, []);
  useEffect(() => {
    // Show Lottie for 10 seconds initially
    lottieTimer.current = setTimeout(() => setLottieVisible(false), 10000);

    // Start the inactivity timer after the initial 10 seconds
    inactivityTimer.current = setTimeout(resetInactivityTimer, 10000);

    // Cleanup timers
    return () => {
      clearTimeout(inactivityTimer.current);
      clearTimeout(lottieTimer.current);
    };
  }, []);

  useEffect(() => {
    // List of events to detect user activity
    const activityEvents = ["mousemove", "keydown", "mousedown", "touchstart"];

    // Add event listeners
    activityEvents.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer)
    );

    // Start inactivity timer initially
    resetInactivityTimer();

    // Cleanup event listeners and timers on component unmount
    return () => {
      clearTimeout(inactivityTimer.current);
      clearTimeout(lottieTimer.current);
      activityEvents.forEach((event) =>
        window.removeEventListener(event, resetInactivityTimer)
      );
    };
  }, []);

  if (config?.IMAGE_SNAPFRAMES?.length === 1) return <ViewMap />;
  if (config && pid && pin >= 0)
    return (
      <>
        <Cursor>
          {!rmode && pin !== 0 && <OverviewBack />}
          <Space d={25} m={120} />
          <SnapOnRotationEnd
            {...{ frame, setFrame, config, setIndex, rmode, isrotate }}
          />
          <Measure ref={measure}>
            <ZoomPanPinch {...{ ref: zppRef, ...zppsetup }}>
              <Tiny {...{ tiny, dim, status, percent }} />
              <Canvas
                {...{ ref: canRef, frame, pid, status, dim, frames, config }}
              />
              <Center {...{ point }} />
              {!rmode && showSVG && <Svg {...{ svg, percent, units }} />}
             
              {/* <Labels {...{ frame, index, config, rmode, scale }} /> */}
              {/* { projectType === 'poly' && <Overview {...{ frame, index, config, rmode, status }}/>} */}
            </ZoomPanPinch>
         
          </Measure>
          <Space d={63} m={110} />
          {isMobile && isLottieVisible && (
                <Box
                onClick={handleLottieClick}
                  sx={{
                    position: "absolute",
                    top: "23%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex:"1"
                  }}
                >
                  <Lottie
                    animationData={mediaLoader}
                    loop={true}
                    style={{ width: "50vh", height: "35vh" }}
                  />
                </Box>
              )}
          <Loading2
            {...{
              progress: percent,
              show: (status !== "DONE" || !units) && frames.length > 1,
            }}
          />
          <AnimatePresence>
            {rmode && (
              <>
                <NoSnapSlider
                  {...{ frame, setFrame, config, frames, index, setIndex }}
                />
                {isrotate && (
                  <Play {...{ frame, setFrame, config, setIndex }} />
                )}
              </>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {!rmode && (
              <>
                {projectType === "poly" && pin !== 0 && (
                  <Status {...{ percent }} />
                )}
                {projectType === "mono" && <Status {...{ percent }} />}
                {/* <ColorLegend/> */}
                {config?.IMAGE_SNAPFRAMES?.length > 1 && (
                  <Controls
                    {...{ frame, setFrame, config, frames, index, setIndex }}
                  />
                )}
              </>
            )}
          </AnimatePresence>
          <Compas
            frame={frame}
            cfg={config}
            label={
              buttonsLabel?.filter(
                (btn) => btn.UILabelProperty === "Nordpil"
              )[0]?.Label
            }
            {...{ percent }}
          />
          <Recenter z={zppRef} bounds={bounds} dim={dim} />

          {/* { !isMobile && <Mode360/> } */}
          {/* { !isMobile && rmode && <Back/> } */}
        </Cursor>
      </>
    );
};
